import React, { useCallback, useMemo, useState } from 'react';
// Deep import — see the import comment in ConcernRegister.jsx / PmlSnapshotPage.jsx.
import { AlpineThemeGrid } from '../../ui/components/Grid';
import { formatUsd } from './hexMapStyles';

const isNil = (v) => v === null || v === undefined;

const summarize = (v) => {
  if (isNil(v)) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
};

// `detail.changes` is a counts object, not an array — `[key, label]` pairs in
// display order. `renewal_basis`/`tiv_change`/`ded_change` are counts of clients
// with that kind of change, not dollar amounts, so they're rendered the same
// "<count> <label>" way as added/removed.
const CHANGE_COUNT_FIELDS = [
  ['added', 'added'],
  ['removed', 'removed'],
  ['renewal_basis', 'renewed'],
  ['tiv_change', 'TIV moved'],
  ['ded_change', 'ded change'],
];

// `detail.changes` = `{added, removed, renewal_basis, tiv_change, ded_change}` —
// zero/absent counts are skipped so "3 added, 20 removed, 5 renewed" doesn't pick
// up trailing "0 TIV moved, 0 ded change" noise.
const formatChanges = (changes) => {
  if (isNil(changes)) return '—';
  const parts = CHANGE_COUNT_FIELDS.map(([key, label]) => {
    const v = changes[key];
    if (isNil(v) || Number(v) === 0) return null;
    return `${v} ${label}`;
  }).filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

// One row per client with movement this month. Exported so the mapping is
// unit-testable without a grid. `idx` is carried through (not shown as a column)
// so a row click can look its rooftop_moves back up by position.
//
// The clients endpoint follows the reporting builders' contract: camelCase
// top-level keys (`name`, `deltaTiv`), but `detail` is nested JSONB passed through
// verbatim, so its keys stay snake_case (`rooftop_moves`, and `changes`'s own
// `renewal_basis`/`tiv_change`/`ded_change` fields) — the casing split below is
// intentional, not a missed rename.
export function clientRowData(clients) {
  return (clients || []).map((c, idx) => ({
    idx,
    name: c.name,
    deltaTiv: c.deltaTiv,
    changes: formatChanges(c.detail?.changes),
    wheres: summarize(c.detail?.wheres),
    rooftopMoves: c.detail?.rooftop_moves || [],
  }));
}

const DELTA_POSITIVE = '#078B80';
const DELTA_NEGATIVE = '#B71C1C';
const DELTA_NEUTRAL = '#454F5B';

const deltaTivCellRenderer = (params) => {
  const v = params.value;
  if (isNil(v)) return '—';
  const n = Number(v);
  const color = n > 0 ? DELTA_POSITIVE : n < 0 ? DELTA_NEGATIVE : DELTA_NEUTRAL;
  return <span style={{ color, fontWeight: 600 }}>{formatUsd(n)}</span>;
};

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  sectionTitle: { fontWeight: 600, fontSize: 14, color: '#212B36' },
  detail: { border: '1px solid #DFE3E8', borderRadius: 4, padding: 12, background: '#F9FAFB' },
  detailTitle: { fontWeight: 600, marginBottom: 8 },
  emptyDetail: { color: '#637381', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', borderBottom: '1px solid #DFE3E8', padding: '4px 8px', color: '#637381' },
  td: { borderBottom: '1px solid #F4F6F8', padding: '4px 8px' },
};

// rooftop_moves entries aren't a fixed shape in the summary payload, so this
// derives its columns from whatever keys the moves actually carry rather than
// hardcoding field names — a plain table, not AG Grid master-detail (the
// AlpineThemeGrid wrapper doesn't expose master-detail props, so it isn't the
// "trivial via the wrapper" case the brief allows for).
const RooftopMoves = ({ moves }) => {
  if (!moves || !moves.length) return <div style={S.emptyDetail}>No rooftop moves recorded.</div>;
  const keys = Array.from(
    moves.reduce((set, m) => {
      Object.keys(m || {}).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  return (
    <table style={S.table}>
      <thead>
        <tr>
          {keys.map((k) => (
            <th key={k} style={S.th}>
              {k}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {moves.map((m, i) => (
          <tr key={i}>
            {keys.map((k) => (
              <td key={k} style={S.td}>
                {isNil(m[k]) ? '—' : String(m[k])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// AG Grid table of client movement (name/ΔTIV/changes/wheres) with a simple
// row-click-to-expand rooftop_moves sub-table underneath — see the interfaces
// note above on why this is a plain table rather than AG Grid master-detail.
const ClientMovement = ({ clients }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  const rowData = useMemo(() => clientRowData(clients), [clients]);

  const columnDefs = useMemo(
    () => [
      { field: 'name', headerName: 'Client' },
      { field: 'deltaTiv', headerName: 'ΔTIV', cellRenderer: deltaTivCellRenderer },
      { field: 'changes', headerName: 'Changes' },
      { field: 'wheres', headerName: 'Where' },
    ],
    []
  );

  const attachRowClick = useCallback((params) => {
    params.api.addEventListener('rowClicked', (event) => {
      const row = event.data;
      if (!row) return;
      setExpandedIdx((prev) => (prev === row.idx ? null : row.idx));
    });
  }, []);

  const expandedClient = !isNil(expandedIdx) ? rowData[expandedIdx] : null;

  return (
    <div data-testid="client-movement" style={S.wrap}>
      <div style={S.sectionTitle}>Client movement</div>
      <AlpineThemeGrid setGridReference={attachRowClick} columnDefs={columnDefs} rowData={rowData} headerBgColor="light-gray" />
      {expandedClient && (
        <div data-testid="rooftop-moves-detail" style={S.detail}>
          <div style={S.detailTitle}>{`${expandedClient.name} — rooftop moves`}</div>
          <RooftopMoves moves={expandedClient.rooftopMoves} />
        </div>
      )}
    </div>
  );
};

export default ClientMovement;
